/**
 * Company: SPHMedia
 * Description: [OptionalLink component renders a link if the 'link' prop is provided, otherwise it renders the children without a link.]
 */

import { ReactElement, ReactNode } from "react";

import Link, { type LinkProps } from "./Link";

type OptionalLinkProps = {
  children: ReactNode;
  link?: Omit<LinkProps, "children">;
};

/**
 * OptionalLink
 * Description: [OptionalLink component renders a link if the 'link' prop is provided, otherwise it renders the children without a link.]
 * @param props - The props required by the component (if any)
 * @returns The rendered React element
 */
export default function OptionalLink({
  link,
  children,
}: OptionalLinkProps): ReactElement {
  if (!link) return <>{children}</>;

  const {
    destination,
    className,
    relativePath,
    target,
    rel,
    type,
    transformCase,
    handleClick,
    testId,
    isMobile,
  } = link;

  const resolvedRelativePath = relativePath ?? true;
  const normalizedDestination =
    resolvedRelativePath && destination.startsWith("/")
      ? destination.slice(1)
      : destination;

  return (
    <Link
      destination={normalizedDestination}
      className={className}
      relativePath={resolvedRelativePath}
      target={target}
      rel={rel}
      type={type}
      transformCase={transformCase}
      handleClick={handleClick}
      testId={testId}
      isMobile={isMobile}
    >
      {children}
    </Link>
  );
}
